/**
 * OKLCH-based theme system for non-game UI.
 * Direction D: Sage (H=170°) + Warm Amber (H=50°) palette.
 * Low-chroma accent recedes so game content pops.
 * All semantic colors defined here; UI files import `theme` for inline styles
 * or use CSS custom properties via `var(--name)` in <style> blocks.
 */

/** Build an oklch() CSS string. L in 0–1, C in 0–0.4, H in degrees. */
export function oklch(l: number, c: number, h: number): string {
  return `oklch(${(l * 100).toFixed(1)}% ${c.toFixed(3)} ${h.toFixed(0)})`;
}

/** Append alpha to an oklch string: oklch(L% C H) → oklch(L% C H / alpha) */
export function withAlpha(color: string, alpha: number): string {
  return color.replace(')', ` / ${alpha})`);
}

export const theme = {
  // Primary accent — sage green
  accent:       oklch(0.68, 0.06, 170),   // sage brand
  accentDim:    oklch(0.55, 0.04, 170),   // dimmer sage
  accentBright: oklch(0.78, 0.08, 170),   // bright emphasis

  // Semantic status
  positive:  oklch(0.72, 0.10, 155),   // green — alive, affordable
  negative:  oklch(0.62, 0.12, 25),    // red — dead, unavailable
  negativeStrong: oklch(0.55, 0.16, 25), // bright red — critical
  warning:   oklch(0.75, 0.12, 55),    // warm orange — caution
  heal:      oklch(0.72, 0.14, 150),   // bright green — heal items
  info:      oklch(0.68, 0.04, 170),   // low-chroma sage — informational
  cyan:      oklch(0.70, 0.05, 185),   // interactive hover
  delivery:  oklch(0.75, 0.12, 55),    // shares warning hue — delivery cost
  indicator: oklch(0.78, 0.10, 50),    // warm amber — urgent indicators

  text: {
    primary:   oklch(0.82, 0.01, 180),   // default body text
    secondary: oklch(0.58, 0.008, 180),  // labels, secondary
    muted:     oklch(0.42, 0.006, 180),  // de-emphasized
    disabled:  oklch(0.35, 0.005, 180),  // disabled controls
    bright:    oklch(0.90, 0.008, 180),  // headers, emphasis
    white:     oklch(1.0, 0, 0),         // #fff
    console:   oklch(0.78, 0.12, 145),   // console success (H=145°)
    modeRule:  oklch(0.70, 0.04, 210),   // mode objectives (H=210°)
  },

  surface: {
    base:          oklch(0.12, 0.01, 180),   // app background
    raised:        oklch(0.17, 0.012, 180),  // elevated panels
    high:          oklch(0.22, 0.015, 180),  // highest elevation
    border:        withAlpha(oklch(0.68, 0.06, 170), 0.3),  // sage border @30%
    borderSubtle:  'rgba(255,255,255,0.06)',
    borderDim:     'rgba(255,255,255,0.03)',
    overlay:       'rgba(0,0,0,0.6)',
    overlayDark:   'rgba(0,0,0,0.75)',
    overlayDeep:   'rgba(0,0,0,0.85)',
    panelBg:       'rgba(16,18,18,0.98)',    // green-gray panel
    endScreenBg:   'rgba(10,12,12,0.98)',    // campaign end
    canvasBg:      'rgba(10,12,12,0.5)',     // preview canvases
  },

  // Derived / convenience — accent alpha variants (sage)
  goldBorder15:  withAlpha(oklch(0.68, 0.06, 170), 0.15),
  goldBorder20:  withAlpha(oklch(0.68, 0.06, 170), 0.2),
  goldBg08:      withAlpha(oklch(0.68, 0.06, 170), 0.08),
  goldBg10:      withAlpha(oklch(0.68, 0.06, 170), 0.1),
  goldBg12:      withAlpha(oklch(0.68, 0.06, 170), 0.12),

  // Row / card backgrounds
  rowBg:         'rgba(255,255,255,0.025)',
  rowBgHover:    'rgba(255,255,255,0.045)',
  rowBgSelected: 'rgba(255,255,255,0.02)',
  chipBg:        'rgba(255,255,255,0.04)',

  // Button helpers
  btnMinus: {
    border: 'rgba(255,255,255,0.10)',
    bg:     'rgba(255,255,255,0.03)',
    text:   oklch(0.58, 0.008, 180),
  },
  btnPositive: {
    border:   'rgba(136,196,160,0.35)',
    bg:       'rgba(136,196,160,0.10)',
    text:     oklch(0.72, 0.10, 155),  // positive green
    bgHover:  'rgba(136,196,160,0.15)',
  },
  btnDisabled: {
    border: oklch(0.28, 0.005, 180),
    bg:     'transparent',
    text:   oklch(0.28, 0.005, 180),
  },
  btnHeal: {
    border: 'rgba(48,180,96,0.35)',
    bg:     'rgba(48,180,96,0.10)',
    text:   oklch(0.72, 0.14, 150),
  },
  healRowBg: 'rgba(48,180,96,0.03)',

  // Campaign difficulty
  difficulty: {
    easy:   oklch(0.72, 0.10, 155),   // green
    medium: oklch(0.68, 0.06, 170),   // sage (accent)
    hard:   oklch(0.72, 0.08, 50),    // amber (warm)
    expert: oklch(0.55, 0.16, 25),    // red
  },

  // Leadership panel — sage-based
  leadership: {
    amber:        oklch(0.68, 0.06, 170),  // accent sage
    cancelBorder: 'rgba(200,80,80,0.4)',
    cancelText:   oklch(0.60, 0.10, 25),
    holdFireBorderActive: 'rgba(200,80,80,0.6)',
    holdFireTextActive:   oklch(0.58, 0.16, 25),
  },

  // Personality chip
  chip: {
    text: oklch(0.62, 0.01, 180),
    bg:   'rgba(255,255,255,0.04)',
  },

  // Aim sphere
  aimSphere: {
    planetFill: 'rgba(16, 18, 18, 0.85)',
    equator:    'rgba(100, 140, 130, 0.3)',
    frontTick:  'rgba(100, 140, 130, 0.5)',
    aimLine:    'rgba(255, 255, 255, 0.9)',
    tipDot:     oklch(1.0, 0, 0),
  },

  // Camera mode colors
  camera: {
    freeBorder: withAlpha(oklch(0.70, 0.05, 185), 0.4),
    freeText:   oklch(0.70, 0.05, 185),   // interactive
    orbitBorder: withAlpha(oklch(0.68, 0.06, 170), 0.4),
    orbitText:  oklch(0.68, 0.06, 170),    // sage
    fpBorder:   withAlpha(oklch(0.78, 0.10, 50), 0.4),
    fpText:     oklch(0.78, 0.10, 50),     // warm amber
  },

  // Unowned system box (campaignMap)
  systemBoxBg: 'rgba(100,130,120,0.06)',
  systemBoxBgOwned: 'rgba(100,130,120,0.08)',

  // Misc
  emptySlotBorder: 'rgba(100,110,108,0.3)',
  selectedRowBg:   'rgba(136,196,160,0.08)',
  buildHoverBg:    'rgba(136,196,160,0.1)',

  // Warning colors (menu.ts)
  warningError:  oklch(0.55, 0.20, 25),
  warningAmber:  oklch(0.75, 0.12, 55),

  // Menu section divider
  sectionDivider: 'rgba(255,255,255,0.06)',
  unitSlotBg:     'rgba(255,255,255,0.02)',
} as const;

/** Inject CSS custom properties on :root for use in <style> blocks */
export function injectThemeVars(): void {
  const style = document.createElement('style');
  style.id = 'theme-vars';
  style.textContent = `
:root {
  /* Semantic colors */
  --accent: ${theme.accent};
  --accent-dim: ${theme.accentDim};
  --accent-bright: ${theme.accentBright};
  --positive: ${theme.positive};
  --negative: ${theme.negative};
  --negative-strong: ${theme.negativeStrong};
  --warning: ${theme.warning};
  --heal: ${theme.heal};
  --info: ${theme.info};
  --cyan: ${theme.cyan};
  --delivery: ${theme.delivery};
  --indicator: ${theme.indicator};

  /* Text */
  --text-primary: ${theme.text.primary};
  --text-secondary: ${theme.text.secondary};
  --text-muted: ${theme.text.muted};
  --text-disabled: ${theme.text.disabled};
  --text-bright: ${theme.text.bright};

  /* Surfaces */
  --surface-base: ${theme.surface.base};
  --surface-raised: ${theme.surface.raised};
  --surface-high: ${theme.surface.high};
  --surface-border: ${theme.surface.border};
  --surface-border-subtle: ${theme.surface.borderSubtle};
  --surface-border-dim: ${theme.surface.borderDim};
  --surface-overlay: ${theme.surface.overlay};
  --surface-overlay-dark: ${theme.surface.overlayDark};
  --surface-panel-bg: ${theme.surface.panelBg};
  --surface-end-screen-bg: ${theme.surface.endScreenBg};

  /* Accent alpha variants */
  --gold-border-15: ${theme.goldBorder15};
  --gold-border-20: ${theme.goldBorder20};
  --gold-bg-08: ${theme.goldBg08};
  --gold-bg-10: ${theme.goldBg10};
  --gold-bg-12: ${theme.goldBg12};

  /* Rows / cards */
  --row-bg: ${theme.rowBg};
  --row-bg-hover: ${theme.rowBgHover};
  --row-bg-selected: ${theme.rowBgSelected};
  --chip-bg: ${theme.chipBg};
  --section-divider: ${theme.sectionDivider};

  /* Button composites */
  --btn-minus-border: ${theme.btnMinus.border};
  --btn-minus-bg: ${theme.btnMinus.bg};
  --btn-minus-text: ${theme.btnMinus.text};
  --btn-positive-border: ${theme.btnPositive.border};
  --btn-positive-bg: ${theme.btnPositive.bg};
  --btn-positive-text: ${theme.btnPositive.text};
  --btn-positive-bg-hover: ${theme.btnPositive.bgHover};
  --btn-disabled-border: ${theme.btnDisabled.border};
  --btn-disabled-text: ${theme.btnDisabled.text};
  --btn-heal-border: ${theme.btnHeal.border};
  --btn-heal-bg: ${theme.btnHeal.bg};
  --btn-heal-text: ${theme.btnHeal.text};
  --heal-row-bg: ${theme.healRowBg};

  /* Leadership */
  --leadership-amber: ${theme.leadership.amber};

  /* Chip */
  --chip-text: ${theme.chip.text};

  /* Build hover */
  --build-hover-bg: ${theme.buildHoverBg};
}
`;
  document.head.appendChild(style);
}
